import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { Creative, Location } from "../../types";
import StandardCreativeUpload from "./media-owner/StandardCreativeUpload";
import AlanCreativeUpload from "./media-owner/AlanCreativeUpload";
import { MapPin } from "lucide-react";

interface PerLocationCreativeItemProps {
  location: Location;
  creatives: Creative[];
  onFileChange: (locationName: string, e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveCreative: (locationName: string, index: number) => void;
  onUpdateCreativeDays?: (locationName: string, creativeIndex: number, days: number[]) => void;
  isUploading: boolean;
  onUrlSubmit?: (locationName: string, url: string, orientation?: 'landscape' | 'portrait') => void;
}

const PerLocationCreativeItem = ({
  location,
  creatives,
  onFileChange,
  onRemoveCreative,
  onUpdateCreativeDays,
  isUploading,
  onUrlSubmit
}: PerLocationCreativeItemProps) => {
  const { t } = useLanguage();
  const mediaOwner = location.mediaOwner || "";
  const isAlan = mediaOwner === "Alan";

  const getDimensionsText = () => {
    if (mediaOwner === "Waki") {
      return "2225 × 3562 px";
    } else if (mediaOwner === "Alan") {
      return "Landscape: 1920 × 1080 px, Portrait: 1080 × 1920 px";
    } else if (mediaOwner === "Attention") {
      return "Outdoor Billboard: 3840 × 2160 px (4K), Digital Signage: 1920 × 1080 px";
    }
    return "1080 × 920 px";
  };

  // Wrapper to pass location.name
  const handleFileChange = (
    _: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    onFileChange(location.name, e);
  };

  const handleRemove = (_: string, index: number) => {
    onRemoveCreative(location.name, index);
  };

  const handleUpdateDays = onUpdateCreativeDays
    ? (_: string, creativeIndex: number, days: number[]) => {
        onUpdateCreativeDays(location.name, creativeIndex, days);
      }
    : undefined;

  const handleUrlSubmit = onUrlSubmit
    ? (_: string, url: string, orientation?: 'landscape' | 'portrait') => {
        onUrlSubmit(location.name, url, orientation);
      }
    : undefined;

  return (
    <div className="border rounded-lg p-4 per-location-creative-item">
      {/* Location Header */}
      <div className="flex items-center gap-2 mb-3">
        <MapPin className="h-4 w-4 text-primary" />
        <div className="flex-1">
          <h4 className="font-medium text-sm">{location.name}</h4>
          <p className="text-xs text-muted-foreground">
            {mediaOwner && <span className="font-medium">{mediaOwner}</span>}
            {mediaOwner && " • "}
            {getDimensionsText()}
          </p>
        </div>
      </div>
      
      {isAlan ? (
        <AlanCreativeUpload
          creatives={creatives}
          onMediaOwnerFileChange={handleFileChange}
          onRemoveMediaOwnerCreative={handleRemove}
          onUpdateMediaOwnerCreativeDays={handleUpdateDays}
          isUploading={isUploading}
          onUrlSubmit={handleUrlSubmit}
        />
      ) : (
        <StandardCreativeUpload
          mediaOwner={location.name}
          creatives={creatives}
          onMediaOwnerFileChange={handleFileChange}
          onRemoveMediaOwnerCreative={handleRemove}
          onUpdateMediaOwnerCreativeDays={handleUpdateDays}
          isUploading={isUploading}
          onUrlSubmit={handleUrlSubmit ? (mo: string, url: string) => handleUrlSubmit(mo, url) : undefined}
        />
      )}
      
      {isUploading && (
        <Alert className="mt-4">
          <AlertDescription className="text-center">
            {t('uploading')}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default PerLocationCreativeItem;
